/**
 * Dashboard hero — calm 3-step journey: People → Memories → Legacy Book.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, Heart, Mic, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { treeHref, personHref } from '../../utils/prettyArchiveLinks';
import { buildTreePeopleAddPersonUrl } from '../../utils/archiveNavigation';
import { legacyBookPreviewHrefFromId } from '../../utils/legacyBookPreview';

const STEP_CLASS =
  'flex h-full flex-col rounded-2xl border border-[hsl(38_16%_82%)]/90 bg-gradient-to-br from-[hsl(42_30%_100%)] via-[hsl(42_28%_99%)] to-[hsl(40_22%_97%)] p-5 shadow-[0_8px_32px_-20px_hsl(28_14%_18%_/_0.12)] sm:p-6';

function JourneyStep({ step, icon: Icon, title, copy, ctaLabel, ctaTo, onCta, testId, disabled }) {
  const cta = (
    <Button
      asChild={Boolean(ctaTo) && !disabled}
      disabled={disabled}
      onClick={onCta}
      className="mt-5 w-full rounded-full bg-[#8B7355] text-white hover:bg-[#7A6548] disabled:opacity-50 sm:w-auto"
      data-testid={testId}
    >
      {ctaTo && !disabled ? (
        <Link to={ctaTo}>
          {ctaLabel}
          <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
        </Link>
      ) : (
        <span>
          {ctaLabel}
          <ChevronRight className="ml-1 inline h-4 w-4" aria-hidden />
        </span>
      )}
    </Button>
  );

  return (
    <article className={STEP_CLASS} data-testid={`core-journey-step-${step}`}>
      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">Step {step}</span>
      <span className="mt-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(38_22%_88%)]/50 text-[#5c4a32]">
        <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
      </span>
      <h3 className="mt-4 font-serif text-xl font-light tracking-tight text-[#1c1917]">{title}</h3>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-[#5c534a]">{copy}</p>
      {cta}
    </article>
  );
}

export function CoreJourneySection({ firstTree, myArchivePersonId, onCreateArchive }) {
  const peopleHref = firstTree ? `${treeHref(firstTree)}?tab=grid` : null;
  const addPersonHref = firstTree ? buildTreePeopleAddPersonUrl(firstTree.id) : null;
  const memoriesHref = myArchivePersonId
    ? `${personHref({ id: myArchivePersonId })}?view=stories`
    : firstTree
      ? `${treeHref(firstTree)}?tab=grid`
      : null;
  const legacyBookHref = myArchivePersonId
    ? legacyBookPreviewHrefFromId(myArchivePersonId)
    : firstTree
      ? `${treeHref(firstTree)}?tab=readiness`
      : null;

  return (
    <section className="space-y-5" data-testid="core-journey-section" aria-labelledby="core-journey-heading">
      <div className="space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">Before memories fade</p>
        <h2 id="core-journey-heading" className="font-serif text-2xl font-light tracking-tight text-[#1c1917] sm:text-[1.65rem]">
          Preserve what matters for your family
        </h2>
        <p className="max-w-2xl text-sm leading-relaxed text-[#5c534a]">
          Add loved ones, save photos and stories, then create your Legacy Book — one calm step at a time.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:gap-5">
        <JourneyStep
          step={1}
          icon={Users}
          title="Add a loved one"
          copy="Start with the people whose stories you want to keep — parents, grandparents, partners, children."
          ctaLabel={firstTree ? 'Open People' : 'Create your archive'}
          ctaTo={peopleHref}
          onCta={!firstTree ? onCreateArchive : undefined}
          testId="core-journey-people"
          disabled={!firstTree && !onCreateArchive}
        />
        <JourneyStep
          step={2}
          icon={Mic}
          title="Save a memory"
          copy="Add photos, voice notes and written stories while they are still close. A few words or one picture is enough to begin."
          ctaLabel={myArchivePersonId ? 'Add photos and stories' : firstTree ? 'Choose someone first' : 'Create your archive'}
          ctaTo={memoriesHref}
          onCta={!firstTree ? onCreateArchive : undefined}
          testId="core-journey-memories"
          disabled={!firstTree}
        />
        <JourneyStep
          step={3}
          icon={BookOpen}
          title="Create your Legacy Book"
          copy="See how memories gather into a beautiful book you can preview and share with family when you are ready."
          ctaLabel={legacyBookHref ? 'Preview Legacy Book' : 'Create your archive'}
          ctaTo={legacyBookHref}
          onCta={!firstTree ? onCreateArchive : undefined}
          testId="core-journey-legacy-book"
          disabled={!firstTree}
        />
      </div>

      <p className="flex items-start gap-2 text-xs leading-relaxed text-[#7a7268]">
        <Heart className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#c9a961]" aria-hidden />
        <span>
          {addPersonHref ? (
            <>
              New here?{' '}
              <Link to={addPersonHref} className="font-medium text-[#5c4a32] underline decoration-[#c9a961]/40 underline-offset-2">
                Add your first loved one
              </Link>{' '}
              — then save a memory from their profile.
            </>
          ) : (
            'Create a private family archive to begin preserving stories for the people you love.'
          )}
        </span>
      </p>
    </section>
  );
}

export default CoreJourneySection;
